import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { motion } from 'framer-motion';
import { MapPin, Users, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para los iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerMap = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Centro de la zona Montevideo-Canelones
  const center = [-34.8, -56.1];

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filtrar solo clientes con ubicación
      const customersWithLocation = response.data.filter(c => c.location);
      setCustomers(customersWithLocation);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Mapa de Clientes</h1>
        <p className="text-gray-600">Visualización geolocalizada de clientes en Montevideo y Canelones</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg" style={{background: 'rgba(212, 175, 55, 0.1)'}}>
              <Users className="h-6 w-6" style={{color: '#D4AF37'}} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Clientes con Ubicación</p>
              <p className="text-2xl font-bold text-gray-800">{customers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-50">
              <MapPin className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Montevideo</p>
              <p className="text-2xl font-bold text-gray-800">
                {customers.filter(c => c.location?.city === 'Montevideo').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-green-50">
              <MapPin className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Canelones</p>
              <p className="text-2xl font-bold text-gray-800">
                {customers.filter(c => c.location?.city === 'Canelones').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{height: '600px'}}>
        <MapContainer 
          center={center} 
          zoom={10} 
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Círculo para destacar la zona de servicio */}
          <Circle
            center={center}
            radius={20000}
            pathOptions={{ 
              color: '#D4AF37', 
              fillColor: '#D4AF37', 
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '10, 10'
            }}
          />

          {/* Marcadores de clientes */}
          {customers.map((customer) => (
            <Marker 
              key={customer.id} 
              position={[customer.location.lat, customer.location.lng]}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-bold text-gray-800 mb-2">{customer.name}</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-3 w-3" />
                      {customer.phone}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-3 w-3" />
                      {customer.location.city}
                    </div>
                  </div>
                  {customer.notes && (
                    <p className="text-xs text-gray-500 mt-2 border-t pt-2">{customer.notes}</p>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {customers.length === 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <MapPin className="h-12 w-12 text-blue-400 mx-auto mb-3" />
          <p className="text-blue-800 font-semibold mb-1">No hay clientes con ubicación</p>
          <p className="text-sm text-blue-600">Los clientes con ubicación geográfica aparecerán en el mapa</p>
        </div>
      )}
    </div>
  );
};

export default CustomerMap;